/**
 * Pricing Request Builder Service
 *
 * Domain service for building pricing estimation requests from order form data.
 * Encapsulates the logic for extracting and transforming form data into
 * pricing API request parameters.
 *
 * This separation ensures:
 * 1. Presentation logic stays in components
 * 2. Domain logic is testable in isolation
 * 3. Consistent request building across the application
 */

import type { OrderForm } from '../schemas/order.schema';
import type { EstimatePriceRequest } from '../types/pricing-estimation';

/**
 * Build a pricing estimation request from order form data
 *
 * Extracts the necessary fields from a partial order form and constructs
 * a properly typed EstimatePriceRequest. Returns null if required fields
 * (containerSize, materialType) are missing.
 *
 * @param form - Partial order form data from react-hook-form
 * @returns EstimatePriceRequest object or null if required fields are missing
 *
 * @example
 * ```ts
 * const formData = watch();
 * const pricingParams = buildPricingRequest(formData);
 * if (pricingParams) {
 *   // Make pricing API call
 * }
 * ```
 */
export function buildPricingRequest(
  form: Partial<OrderForm>
): EstimatePriceRequest | null {
  // Require both containerSize and materialType for valid pricing request
  if (!form.containerSize || !form.materialType) {
    return null;
  }

  return {
    containerSize: form.containerSize,
    materialType: form.materialType,
    selectedServices: form.selectedServices,
    // PRRO fields are only present in NewDeliveryForm
    // Use type assertion after checking for their existence (they're optional in the request)
    estimatedTonnage: 'estimatedTonnage' in form ? form.estimatedTonnage : undefined,
    hasTreatedWood: 'hasTreatedWood' in form ? form.hasTreatedWood : undefined,
    estimatedWeightTons: 'estimatedWeightTons' in form ? form.estimatedWeightTons : undefined,
  };
}
